import React, { Component } from "react";
import styled, { keyframes } from "styled-components";

import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import { Slide } from "js/core/models/slide";
import { $, _ } from "js/vendor";
import getObjectHash from "common/utils/getObjectHash";
import { themeColors } from "../sharedStyles";

const logger = getLogger(LogGroup.CANVAS);

const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

const Container = styled.div.attrs({ "data-test-id": "canvas-preview" })`
    position: relative;
`;

const CanvasContainer = styled.div`
    position: relative;
    outline: solid 1px ${props => props.outlineColor || "#ccc"};
    overflow: hidden;
    transition: opacity 500ms;

    &:hover {
        outline-width: 2px;
        outline-color: ${props => props.hoverOutlineColor || themeColors.ui_blue};
    }
`;

const CanvasFrame = styled.div`
    width: 1280px;
    height: 720px;
    //outline: solid 1px #ccc;
    overflow: hidden;
    cursor: pointer;
    pointer-events: none;
`;

const ClickShield = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
`;

const Skeleton = styled.div.attrs(({ width, height, visible }) => ({
    style: {
        width: width,
        height: height,
        opacity: visible ? 1 : 0
    }
}))`
    position: absolute;
    background: #666;
    transition: opacity 300ms;
`;

class CanvasPreview extends Component {
    constructor() {
        super();

        this.ref = React.createRef();

        this.renderPromise = Promise.resolve();
        this.touchStartX = null;
        this.touchStartY = null;

        this.state = {
            isRendering: true
        };
    }

    componentDidMount() {
        const { slideModel, width, theme, onRendered, onCanvasRendered, animateOnMount } = this.props;

        this.renderModelKey = getObjectHash(slideModel);

        this.renderPromise = (async () => {
            const { SlideCanvas } = await getCanvasBundle(appVersion);
            const { CanvasEventType } = await import("common/constants");

            try {
                const model = new Slide(_.cloneDeep(slideModel), { disconnected: true });

                this.slideCanvas = new SlideCanvas({
                    dataModel: model,
                    canvasWidth: 1280,
                    canvasHeight: 720,
                    editable: false,
                    theme
                });
                $(this.ref.current).append(this.slideCanvas.render().$el);

                this.slideCanvas.$el.css({ transform: `scale(${width / 1280})` });

                // Listen for canvas render completion
                if (onCanvasRendered) {
                    this.slideCanvas.once(CanvasEventType.RENDER, () => {
                        onCanvasRendered();
                    });
                }

                await this.slideCanvas.renderSlide();

                onRendered && onRendered();

                // Trigger animation on mount if requested
                if (animateOnMount) {
                    this.animateCanvas();
                }

                this.setState({ isRendering: false });
            } catch (err) {
                logger.error(err, "[CanvasPreview] componentDidMount() renderPromise failed", { slideId: this.props.slideModel?.id });
            }
        })();
    }

    componentDidUpdate(prevProps) {
        const { onRendered = () => { } } = this.props;

        this.renderPromise = this.renderPromise.then(async () => {
            try {
                if (getObjectHash(prevProps.slideModel) !== getObjectHash(this.props.slideModel)) {
                    logger.info("[CanvasPreview] componentDidUpdate() slideModel changed");
                    this.slideCanvas.dataModel.update(_.cloneDeep(this.props.slideModel), { removeMissingKeys: true, silent: true });
                    await this.slideCanvas.handleDataStateUpdate();
                    onRendered();
                    return;
                }

                let shouldRefresh = false, shouldRenderModel = false;
                if (prevProps.theme !== this.props.theme) {
                    logger.info("[CanvasPreview] componentDidUpdate() theme changed");
                    this.slideCanvas.options.theme = this.props.theme;
                    shouldRenderModel = true;
                }

                if (prevProps.slideColor !== this.props.slideColor) {
                    logger.info("[CanvasPreview] componentDidUpdate() slideColor changed");
                    this.slideCanvas.model.layout.slideColor = this.props.slideColor;
                    shouldRefresh = true;
                }

                if (prevProps.backgroundColor !== this.props.backgroundColor) {
                    logger.info("[CanvasPreview] componentDidUpdate() backgroundColor changed");
                    this.slideCanvas.model.layout.backgroundStyle = this.props.backgroundStyle;
                    this.slideCanvas.model.layout.backgroundColor = this.props.backgroundColor;
                    shouldRefresh = true;
                }

                if (shouldRenderModel) {
                    await this.slideCanvas.renderModel(true);
                    onRendered();
                    return;
                }

                if (shouldRefresh) {
                    await this.slideCanvas.refreshCanvas();
                    onRendered();
                }
            } catch (err) {
                logger.error(err, "[CanvasPreview] componentDidUpdate() failed", { slideId: this.props.slideModel?.id });
            }
        });
    }

    animateCanvas = () => {
        const { onAnimationComplete } = this.props;
        this.slideCanvas.prepareForAnimation().then(() => {
            this.slideCanvas.animate(() => {
                onAnimationComplete && onAnimationComplete();
            }, false);
        });
    }

    handleTouchStart = e => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
    }

    handleTouchEnd = e => {
        if (this.touchStartX === null || this.touchStartY === null) {
            return;
        }

        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;

        const deltaX = Math.abs(touchEndX - this.touchStartX);
        const deltaY = Math.abs(touchEndY - this.touchStartY);

        // Only animate if movement is minimal (< 10px) - this was a tap, not a drag
        if (deltaX < 10 && deltaY < 10) {
            this.animateCanvas();
        }

        this.touchStartX = null;
        this.touchStartY = null;
    }

    render() {
        const { isRendering } = this.state;
        const { position = "absolute", translateX = 0, translateY = 0, scale = 1, transition, onClick, animateOnHover, shadow = true, outlineColor = "#ccc", hoverOutlineColor } = this.props;

        let style;
        let containerPositionStyle;
        if (position === "absolute") {
            containerPositionStyle = {
                position: "absolute",
                transform: `translateX(${translateX}px) translateY(${translateY}px)`,
                transformOrigin: "0 0",
                transition: transition ? "transform 300ms" : "none"
            };
        }
        style = {
            transform: `scale(${scale})`,
            transformOrigin: "0 0"
        };

        let width = 1280 * scale;
        let containerStyle = {
            width: width,
            height: (720 / 1280) * width,
            opacity: isRendering ? 0 : 1
        };

        return (
            <Container style={containerPositionStyle}>
                <Skeleton width={containerStyle.width} height={containerStyle.height} visible={isRendering} />
                <CanvasContainer style={containerStyle} outlineColor={outlineColor} hoverOutlineColor={hoverOutlineColor} onClick={onClick}>
                    <CanvasFrame
                        ref={this.ref}
                        className="canvas-frame"
                        shadow={shadow}
                        animateOnHover={animateOnHover}
                        {...(animateOnHover ? {
                            onMouseEnter: this.animateCanvas,
                            onTouchStart: this.handleTouchStart,
                            onTouchEnd: this.handleTouchEnd
                        } : {})}
                        style={style}
                    />
                    <ClickShield />
                </CanvasContainer>
            </Container>
        );
    }
}

export default CanvasPreview;
